import { useState } from "react";
import { UtensilsCrossed } from "lucide-react";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { useI18n } from "@/lib/i18n";
import { useStorefront } from "@/lib/storefront";
import type { ResponsiveImageSource } from "@/lib/types";
import { cn } from "@/lib/utils";

export function MenuItemImage({
  source,
  name,
  sizes,
  className,
  fallbackClassName,
  loading,
}: {
  source?: ResponsiveImageSource | null;
  name: string;
  sizes: string;
  className?: string;
  fallbackClassName?: string;
  loading?: "eager" | "lazy";
}) {
  const { t } = useI18n();
  const { tenant } = useStorefront();
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const [failedLogo, setFailedLogo] = useState<string | null>(null);
  const imageUrl = typeof source === "string" ? source : source?.src;
  const logo = tenant.logoImage ?? tenant.logoUrl;
  const logoUrl = typeof logo === "string" ? logo : logo?.src;

  if (source && imageUrl?.trim() && failedImage !== imageUrl) {
    return (
      <ResponsiveImage
        source={source}
        sizes={sizes}
        alt={name}
        loading={loading}
        onError={() => setFailedImage(imageUrl)}
        className={cn("block", className)}
      />
    );
  }

  return (
    <div
      role="img"
      aria-label={t("menu.photoMissing", { name })}
      className={cn(
        "grid place-items-center overflow-hidden bg-white text-muted-foreground",
        className,
        fallbackClassName,
      )}
    >
      {logo && logoUrl?.trim() && failedLogo !== logoUrl ? (
        <ResponsiveImage
          source={logo}
          sizes={sizes}
          alt=""
          loading={loading}
          onError={() => setFailedLogo(logoUrl)}
          className="max-h-[72%] max-w-[72%] object-contain"
        />
      ) : (
        <UtensilsCrossed aria-hidden="true" className="size-7 opacity-55" />
      )}
    </div>
  );
}
